import React, { useRef, useState, useCallback, useEffect } from 'react'
import classNames from 'classnames'
import PropTypes from 'prop-types'
import { useConfig } from '../ConfigProvider'
import cloneDeep from 'lodash/cloneDeep'
import FileItem from './FileItem'
import Button from '../Buttons'
import CloseButton from '../CloseButton'
import Notification from '../Notification'
import toast from '../toast'
import ImageCropper from '../ImageCropper'
import { HiPhotograph } from 'react-icons/hi'

const filesToArray = (files) => Object.keys(files).map((key) => files[key])

const UploadWithCrop = React.forwardRef((props, ref) => {
    const {
        accept,
        beforeUpload,
        disabled,
        draggable,
        fileList,
        multiple,
        onChange,
        onFileRemove,
        showList,
        tip,
        uploadLimit,
        children,
        className,
        field,
        form,
        // Crop-specific props
        enableCrop = true,
        cropAspectRatio = null,
        cropPresets = [],
        cropTitle = 'Crop Image',
        onCropComplete,
        ...rest
    } = props

    const fileInputField = useRef(null)
    const [files, setFiles] = useState(fileList)
    const [dragOver, setDragOver] = useState(false)
    const [cropperOpen, setCropperOpen] = useState(false)
    const [currentImageForCrop, setCurrentImageForCrop] = useState(null)

    const { themeColor, primaryColorLevel } = useConfig()

    useEffect(() => {
        setFiles(fileList)
    }, [fileList])

    // Cleanup effect to prevent memory leaks
    useEffect(() => {
        return () => {
            if (currentImageForCrop) {
                URL.revokeObjectURL(currentImageForCrop.url)
            }
        }
    }, [currentImageForCrop])

    const triggerMessage = (msg) => {
        toast.push(
            <Notification type="danger" duration={2000}>
                {msg || 'Upload Failed!'}
            </Notification>,
            {
                placement: 'top-center',
            }
        )
    }

    const pushFile = (newFiles, file) => {
        for (let f of newFiles) {
            file.push(f)
        }
        return file
    }

    const addNewFiles = useCallback((newFiles) => {
        let file = cloneDeep(files)
        if (typeof uploadLimit === 'number' && uploadLimit !== 0) {
            if (Object.keys(file).length >= uploadLimit) {
                if (uploadLimit === 1) {
                    file.shift()
                    file = pushFile(newFiles, file)
                }

                return filesToArray({ ...file })
            }
        }
        file = pushFile(newFiles, file)
        return filesToArray({ ...file })
    }, [files, uploadLimit])

    const isImageFile = (file) => {
        return file.type && file.type.startsWith('image/')
    }

    const onNewFileUpload = (e) => {
        const { files: newFiles } = e.target
        let result = true

        // Clear any existing image for crop to prevent cache issues
        if (currentImageForCrop) {
            URL.revokeObjectURL(currentImageForCrop.url)
            setCurrentImageForCrop(null)
        }

        if (beforeUpload) {
            result = beforeUpload(newFiles, files)

            if (result === false) {
                triggerMessage()
                return
            }

            if (typeof result === 'string' && result.length > 0) {
                triggerMessage(result)
                return
            }
        }

        if (result) {
            const filesArray = Array.from(newFiles)

            // If crop is enabled and we have image files, show cropper for the first image
            if (enableCrop && filesArray.length > 0 && isImageFile(filesArray[0])) {
                const file = filesArray[0]
                // Create a unique URL with timestamp to prevent caching issues
                const imageUrl = URL.createObjectURL(file)
                setCurrentImageForCrop({
                    file,
                    url: imageUrl,
                    originalFiles: filesArray,
                    timestamp: Date.now() // Add timestamp to ensure uniqueness
                })
                setCropperOpen(true)
            } else {
                // Process files normally without cropping
                let updatedFiles = addNewFiles(filesArray)
                setFiles(updatedFiles)
                onChange?.(updatedFiles, files)
            }
        }

        // Reset the file input to allow the same file to be selected again
        if (fileInputField.current) {
            fileInputField.current.value = ''
        }
    }

    const handleCropComplete = useCallback((croppedBlob) => {
        if (!currentImageForCrop) return

        // Create a new File object from the cropped blob with unique name to prevent caching
        const timestamp = Date.now()
        const fileExtension = currentImageForCrop.file.name.split('.').pop() || 'jpg'
        const uniqueFileName = `${currentImageForCrop.file.name.split('.')[0]}_cropped_${timestamp}.${fileExtension}`

        const croppedFile = new File(
            [croppedBlob],
            uniqueFileName,
            {
                type: currentImageForCrop.file.type,
                lastModified: timestamp
            }
        )

        // For crop workflow, we don't process files here since onCropComplete handles everything
        // Only process files if no crop completion handler is provided
        if (!onCropComplete) {
            const remainingFiles = currentImageForCrop.originalFiles.slice(1)
            const allFiles = [croppedFile, ...remainingFiles]
            let updatedFiles = addNewFiles(allFiles)
            setFiles(updatedFiles)
            onChange?.(updatedFiles, files)
        } else {
            // When cropping is enabled, we don't call onChange at all
            // The onCropComplete handler manages the form state
        }

        // Call custom crop complete handler if provided
        onCropComplete?.(croppedFile)

        // Cleanup - revoke the object URL to prevent memory leaks
        URL.revokeObjectURL(currentImageForCrop.url)
        setCurrentImageForCrop(null)
        setCropperOpen(false)
    }, [addNewFiles, currentImageForCrop, files, onChange, onCropComplete])

    const handleCropCancel = useCallback(() => {
        if (currentImageForCrop) {
            URL.revokeObjectURL(currentImageForCrop.url)
            setCurrentImageForCrop(null)
        }
        setCropperOpen(false)

        // Reset the file input to allow the same file to be selected again
        if (fileInputField.current) {
            fileInputField.current.value = ''
        }
    }, [currentImageForCrop])

    const removeFile = (fileIndex) => {
        const deletedFileList = files.filter((_, index) => index !== fileIndex)
        setFiles(deletedFileList)
        onFileRemove?.(deletedFileList)
    }

    const triggerUpload = (e) => {
        if (!disabled) {
            fileInputField.current?.click()
        }
        e.stopPropagation()
    }

    const renderChildren = () => {
        if (!draggable && !children) {
            return (
                <Button disabled={disabled} onClick={(e) => e.preventDefault()}>
                    Upload{enableCrop && ' & Crop'}
                </Button>
            )
        }

        if (draggable && !children) {
            return (
                <span>
                    Choose a file or drag and drop here
                    {enableCrop && (
                        <span className="block text-xs text-gray-500 mt-1">
                            <HiPhotograph className="inline mr-1" />
                            Images will be cropped before upload
                        </span>
                    )}
                </span>
            )
        }

        return children
    }

    const handleDragLeave = useCallback(() => {
        if (draggable) {
            setDragOver(false)
        }
    }, [draggable])

    const handleDragOver = useCallback(() => {
        if (draggable && !disabled) {
            setDragOver(true)
        }
    }, [draggable, disabled])

    const handleDrop = useCallback(() => {
        if (draggable) {
            setDragOver(false)
        }
    }, [draggable])

    const draggableProp = {
        onDragLeave: handleDragLeave,
        onDragOver: handleDragOver,
        onDrop: handleDrop,
    }

    const draggableEventFeedbackClass = `border-${themeColor}-${primaryColorLevel}`

    const uploadClass = classNames(
        'upload',
        draggable && `upload-draggable`,
        draggable && !disabled && `hover:${draggableEventFeedbackClass}`,
        draggable && disabled && 'disabled',
        dragOver && draggableEventFeedbackClass,
        className
    )

    const uploadInputClass = classNames(
        'upload-input',
        draggable && `draggable`
    )

    return (
        <>
            <div
                ref={ref}
                className={uploadClass}
                {...(draggable ? draggableProp : { onClick: triggerUpload })}
                {...rest}
            >
                <input
                    className={uploadInputClass}
                    type="file"
                    ref={fileInputField}
                    onChange={onNewFileUpload}
                    disabled={disabled}
                    multiple={multiple}
                    accept={accept}
                    title=""
                    value=""
                    {...field}
                    {...rest}
                ></input>
                {renderChildren()}
            </div>
            {tip}
            {showList && (
                <div className="upload-file-list">
                    {files.map((file, index) => (
                        <FileItem file={file} key={file.name + index}>
                            <CloseButton
                                onClick={() => removeFile(index)}
                                className="upload-file-remove"
                            />
                        </FileItem>
                    ))}
                </div>
            )}

            {/* Image Cropper Modal */}
            <ImageCropper
                isOpen={cropperOpen}
                onClose={handleCropCancel}
                imageSrc={currentImageForCrop?.url}
                onCropComplete={handleCropComplete}
                aspectRatio={cropAspectRatio}
                title={cropTitle}
                cropPresets={cropPresets}
                originalFile={currentImageForCrop?.file}
            />

        </>
    )
})

UploadWithCrop.propTypes = {
    uploadLimit: PropTypes.number,
    draggable: PropTypes.bool,
    disabled: PropTypes.bool,
    showList: PropTypes.bool,
    multiple: PropTypes.bool,
    accept: PropTypes.string,
    tip: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
    enableCrop: PropTypes.bool,
    cropAspectRatio: PropTypes.number,
    cropPresets: PropTypes.array,
    cropTitle: PropTypes.string,
    onCropComplete: PropTypes.func,
}

UploadWithCrop.defaultProps = {
    draggable: false,
    showList: true,
    disabled: false,
    fileList: [],
    enableCrop: true,
    cropAspectRatio: null,
    cropPresets: [],
    cropTitle: 'Crop Image',
}

export default UploadWithCrop
